// 個人情報取扱等同意確認
import React, { useState, useEffect } from 'react';
import { render } from 'react-dom';
import axios from '../../axios';
import { handleBrowserBack } from '../../BrowserBack';
import { clauses_list, reception_type } from '../../Const';
import AgreementTemplate from '../../molecules/AgreementTemplate';
import TransitionButton from '../../molecules/TransitionButton';
import Annotation from '../../atoms/Annotation';
import { isBrowserIe } from '../../BrowserJudgment';
import LoadingModal from '../../molecules/LoadingModal';

interface Clauses {
  heading: string;
  types: number[];
  main_btn_label: string;
  sub_btn_label: string;
  leave_text: string;
  sub_btn_area: JSX.Element | undefined;
}

const AgreementPersonalInfo: React.FC = () => {
  // URLパラメータ取得
  const url_params = new URLSearchParams(document.location.search.substring(1));
  // URLパラメータから金融機関コードを取得
  const jacode = url_params.get('jacode');
  // URLパラメータから案件種別IDを取得
  const r_type_id = url_params.get('r_type_id');
  // URLパラメータからローン種類CDを取得
  const loan_type_cd = url_params.get('loan_type_cd');
  // URLパラメータから案件番号を取得
  const r_cd = url_params.get('r_cd');
  // URLパラメータから案件IDを取得
  const r_id = url_params.get('r_id');
  // ローディング画面表示制御
  const [is_loading, setIsLoading] = useState(false);
  // 画面構成
  const [clauses, setClauses] = useState<Clauses>(clauses_list[2]);
  // 同意文の件名、本文のstate
  const [agreement_text, setAgreementText] = useState([]);

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
    fetchLists();
    checkReceptionType();
  }, []);

  // 同意文の取得
  const fetchLists = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${location.origin}${process.env.MIX_MOCK_URL}/get_agreement_personal_info`, {
        params: {
          clause_types: clauses.types,
          ja_code: jacode,
        },
      });

      if (response.data.result) {
        setAgreementText(response.data.clauses);
      } else {
        console.error(response.data.message);
        alert(response.data.message);
      }
    } catch (error) {
      console.error(error);
      console.error('システムエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  // 案件種別によって表示を変える
  const checkReceptionType = () => {
    switch (r_type_id) {
      case reception_type.borrowing_consultation: // 借入相談の場合
        return setClauses(prev => {
          return {
            ...prev,
            heading: '借入相談',
            main_btn_label: '同意して借入相談へ進む',
            sub_btn_area: (
              <TransitionButton
                label="マイページへ戻る"
                btn_class="two_lines"
                handleClick={transitionMypage}
              />
            ),
          };
        });
      case reception_type.preliminary_review: // 事前審査の場合
        return setClauses(prev => {
          return {
            ...prev,
            heading: '事前審査申込',
            main_btn_label: '同意して事前審査へ進む',
            sub_btn_area: (
              <TransitionButton label={clauses.sub_btn_label} handleClick={subButtonClick} />
            ),
          };
        });
      case reception_type.final_review: // 本審査の場合
        return setClauses(prev => {
          return {
            ...prev,
            heading: '借入申込',
            main_btn_label: '同意して借入申込へ進む',
            sub_btn_area: (
              <TransitionButton
                label="マイページへ戻る"
                btn_class="two_lines"
                handleClick={transitionMypage}
              />
            ),
          };
        });
      case null: // ネットローンHPからの場合
        return setClauses(prev => {
          return {
            ...prev,
            heading: '事前審査申込',
            main_btn_label: '同意して事前審査へ進む',
            sub_btn_area: <Annotation text={clauses.leave_text} />,
          };
        });
      default:
        return;
    }
  };

  // 画面下部中央のボタンを押下した時
  const mainButtonClick = async () => {
    try {
      setIsLoading(true);

      // 同意文IDのリストを作成
      const clauses_id_list = agreement_text.map(value => {
        return value['id'];
      });

      // 登録処理
      const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/api/register_personal_info`, {
        ja_code: jacode,
        loan_type_cd: loan_type_cd,
        clauses_id_list: clauses_id_list,
        reception_type_id: r_type_id ?? reception_type.preliminary_review, // nullの場合は2を入れる
        reception_cd: r_cd,
        reception_id: r_id,
      });

      // 画面遷移
      location.replace(`${response.data}`);
    } catch (error) {
      console.error(error);
      alert('システムエラーが発生しました');
      setIsLoading(false);
    }
  };

  // 画面左下の戻るボタンを押下した時
  const subButtonClick = () => {
    setIsLoading(true);
    if (isBrowserIe()) {
      const node = document.getElementById('agreement-personal-info');
      // 遷移先URLを設定
      const url =
        document.referrer === ''
          ? `${location.origin}${process.env.MIX_MOCK_URL}/mypage/list`
          : (node?.dataset.referer as string);
      location.replace(url);
    } else {
      // 遷移先URLを設定
      const url =
        document.referrer === ''
          ? `${location.origin}${process.env.MIX_MOCK_URL}/mypage/list`
          : document.referrer;
      location.replace(url);
    }
  };

  // マイページへ戻るボタン押下
  const transitionMypage = () => {
    setIsLoading(true);
    location.href = `${location.origin}${process.env.MIX_MOCK_URL}/mypage/list`;
  };

  return (
    <>
      <AgreementTemplate
        agreement_text={agreement_text}
        clauses={clauses}
        main_btn_func={() => mainButtonClick()}
      />
      <LoadingModal show={is_loading} />
    </>
  );
};

if (document.getElementById('agreement-personal-info')) {
  render(<AgreementPersonalInfo />, document.getElementById('agreement-personal-info'));
}
